import { jdiColorFetch } from '@/apis/colorFetchV2';
import { AddAddress, Address, CountyData, ListData } from './address.interface';

/*
 * https://joyspace.jd.com/pages/iFAEYMJLLB1eAX3tEDDH
 * @interface Data
 */

interface Response<T> {
  success: boolean;
  code: string;
  msg: string;
  data: T;
  traceId: string;
  result: boolean;
}

// 查询用户地址列表
export const getUserAddressList = (params?: {
  source?: number;
  displayOverArea?: boolean;
  deliverPile?: boolean;
}): Promise<Response<ListData>> => {
  // 设置默认值为 0
  const finalParams = { source: 0, ...params };
  return jdiColorFetch(
    `user_address_getList`,
    {
      method: 'get',
      bodyQuery: {
        ...finalParams,
      },
    },
    {
      ignoreForceLogin: true, // 跳过未登录重定向
    },
  );
};

// 查询county数据列表（地址新增页面-下拉选数据）
export const getCountyList = (): Promise<CountyData> => {
  return jdiColorFetch(`user_address_getCounties`, {
    method: 'get',
    bodyQuery: {},
  })
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 新增用户地址
export const addAddress = (requestData: AddAddress): Promise<Response<any>> => {
  return jdiColorFetch(
    `user_address_add`,
    {
      method: 'post',
      bodyQuery: { ...requestData },
    },
    { isCommonArea: false },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 更新用户地址
export const updateAddress = (requestData: AddAddress): Promise<Response<any>> => {
  return jdiColorFetch(
    `user_address_modify`,
    {
      method: 'post',
      bodyQuery: { ...requestData },
    },
    { isCommonArea: false },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 删除用户地址
export const deleteAddress = (requestData: { addressId: number }): Promise<Response<any>> => {
  return jdiColorFetch(
    `user_address_delete`,
    {
      method: 'post',
      bodyQuery: requestData,
    },
    { isCommonArea: false },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 查询postcode接口（用于校验postcode是否有效及获取四级地址）
export const queryPostCode = (requestData: {
  postCode: string;
  countryId?: string;
  countryCode?: string;
  checkDeliverableFlag?: boolean;
}): Promise<Response<Address>> => {
  return jdiColorFetch(
    `user_address_getPostCode`,
    {
      method: 'get',
      bodyQuery: requestData,
    },
    { isCommonArea: false },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 地址自动补全接口
export const autocomplete = (requestData: {
  searchText: string;
  countryId?: string;
  countryCode?: string;
}): Promise<Response<any>> => {
  return jdiColorFetch(
    `user_address_autoComplete`,
    {
      method: 'get',
      bodyQuery: requestData,
    },
    { isCommonArea: false },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 地址详细信息接口
export const placeDetail = (requestData: {
  placeId: string;
  version?: string;
}): Promise<Response<any>> => {
  return jdiColorFetch(
    `user_address_getDetail`,
    {
      method: 'get',
      bodyQuery: requestData,
    },
    { isCommonArea: false },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 删除用户联登草稿地址 v315
export const deleteDraftAddress = (requestData: { addressId: number }): Promise<Response<any>> => {
  return jdiColorFetch(
    `user_address_deleteDraft`,
    {
      method: 'post',
      bodyQuery: requestData,
    },
    { isCommonArea: false },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 通过经纬度获取地址
export const getLocationDetail = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(`user_address_geoDetail`, {
    method: 'get',
    bodyQuery: requestData,
  })
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 查询全量国家信息列表接口
export const getCountryInfoList = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(
    `base_address_getCountryList`,
    {
      method: 'post',
      bodyQuery: requestData,
    },
    {
      isCommonArea: false, // 跳过地址body查询 避免死循环
    },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 查询推荐自提点地址列表
export const getRecommendPickUpList = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(
    `base_address_getRecommendPickUpList`,
    {
      method: 'post',
      bodyQuery: requestData,
    },
    {
      isCommonArea: false, // 不传地址公共参数
      ignoreForceLogin: true, // 跳过未登录重定向
    },
  )
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 查询用户收藏自提点地址列表
export const getSavedPickUpList = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(`base_address_getSavedPickUpList`, {
    method: 'post',
    bodyQuery: requestData,
  })
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 查询自提点详情
export const getPickUpDetail = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(`base_address_getPickUpDetail`, {
    method: 'post',
    bodyQuery: requestData,
  })
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 用户自提点数据编辑（1：收藏 2：取消收藏 3：设定默认 4：取消默认）
export const modifyPickUp = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(`base_address_modifyPickUp`, {
    method: 'post',
    bodyQuery: requestData,
  })
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 常用地址校验规则 user_address_getRule
export const getAddressRule = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(`user_address_getRule`, {
    method: 'post',
    bodyQuery: requestData,
  })
    .then((response: any) => response)
    .catch((error: any) => error);
};

// 获取地址编辑表单配置
export const getAddressEditForm = (requestData: any): Promise<Response<any>> => {
  return jdiColorFetch(`base_address_getComponentList`, {
    method: 'get',
    bodyQuery: requestData,
  })
    .then((response: any) => response)
    .catch((error: any) => error);
};
